import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'
import {
  getBonusStats,
  getUserTasks,
  getReferralUsers,
  claimRewards,
  type ClaimRewardsRequest,
  type TasksResponse,
} from '../api/bonusApi'

// ============= Query Hooks =============

/**
 * Fetch bonus stats for a wallet
 */
export function useBonusStats() {
  return useQuery({
    queryKey: ['bonus', 'stats'],
    queryFn: () => {
      return getBonusStats()
    },
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Refetch every 60 seconds
  })
}

/**
 * Fetch user tasks
 * Works with or without wallet - without wallet returns default tasks
 */
export function useUserTasks(): ReturnType<typeof useQuery<TasksResponse>> {
  return useQuery({
    queryKey: ['bonus', 'tasks'],
    queryFn: () => getUserTasks(),
    // Always enabled - backend returns default tasks if no wallet
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Refetch every 60 seconds
  })
}

/**
 * Fetch referral users with infinite scroll pagination
 */
export function useReferralUsers(limit: number = 50, offset: number = 0) {
  return useInfiniteQuery({
    queryKey: ['bonus', 'referral'],
    queryFn: () => {
      return getReferralUsers(limit, offset)
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (sum, page) => sum + page.referrals.length,
        0,
      )
      if (loadedCount >= lastPage.pagination.total) {
        return undefined
      }
      return loadedCount
    },
    initialPageParam: 0,
    staleTime: 60_000, // 60 seconds (less frequently changing)
  })
}

// ============= Mutation Hooks =============

/**
 * Claim rewards mutation
 */
export function useClaimRewards() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ClaimRewardsRequest) => claimRewards(request),

    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'stats', variables.walletAddress],
      })

      queryClient.invalidateQueries({
        queryKey: ['bonus', 'tasks', variables.walletAddress],
      })

      queryClient.invalidateQueries({ queryKey: ['bonus'] })
    },
  })
}
